import React, { useContext, useEffect, useState } from 'react';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Link } from '@uhg-abyss/web/ui/Link';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { Card } from '@uhg-abyss/web/ui/Card';
import { SelectInput } from '@uhg-abyss/web/ui/SelectInput';
import { Checkbox } from '@uhg-abyss/web/ui/Checkbox';
import { cellValueHighLight } from '@src/utils/tableUtils';
import { Button } from '@uhg-abyss/web/ui/Button';
import { ModalDialog } from '@uhg-abyss/web/ui/ModalDialog';
import { Box } from '@uhg-abyss/web/ui/Box';
import { LoadingScreen } from '@src/common/LoadingScreen';
import { useOverlay } from '@uhg-abyss/web/hooks/useOverlay';
import { Table } from '@src/common/Table';
import CryptoJS from 'crypto-js';
import { encdata } from '@src/constants';
import {
  useLazyGetReportsQuery,
  useLazySetReprocessRejectChangeOwnerAllQuery,
  useLazySetReprocessRejectChangeOwnerQuery,
} from '@src/store/services/cdb/onlineServices';
import { getServiceURL } from '@src/env.config';
import {
  useLazyGetGPSReportsQuery,
  useLazyGetGpsENWChildReportsQuery,
} from '@src/store/services/gps/onlineServices';
import { FileContext } from '@src/routes/Routes';
import { useAuth } from '@src/Providers/AuthProvider';
import { maskUsingUserAccess } from '@src/utils';

const getDisabledState = (reprocessMethod) => {
  switch (reprocessMethod) {
    case 'Checked Transactions':
      return false;
    case 'Reprocess All':
      return true;
    case 'Reprocess All eligible EEA Errors':
      return false;
    case 'File Processing Date':
      return true;
    case 'Reject All':
      return true;
    case 'Change Owner All':
      return true;
    default:
      return true;
  }
};

export const EligibilityAnalystCorrections = ({ procStatus }) => {
  const [transactionData, setTransactionData] = useState<
    Record<string, string>[]
  >([]);
  const [reprocessMethod, setReprocessMethod] = useState(
    'Checked Transactions'
  );
  const [disableCheckbox, setDisableCheckbox] = useState(false);
  const [fileProcessDate, setFileProcessDate] = useState('');
  const [trigger] = useLazySetReprocessRejectChangeOwnerQuery();
  const [allTrigger] = useLazySetReprocessRejectChangeOwnerAllQuery();
  const [isPromptVisible, setIsPromptVisible] = useState(false);
  const [actionType, setActionType] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const { user } = useAuth();

  const [isExtType, setIsExtType] = useState(
    user?.userType === 'E' ? true : false
  );
  const router = useRouter();
  const { service, fieldCode, errorCode } = router.getRouteParams();
  const distinationSystem = service === 'gps' ? '/gps' : 'cdb';

  const modal = useOverlay('process-modal');
  const { data: modalData } = modal.getState();

  const { fileData } = useContext(FileContext);
  const errorStatuses = [
    'COMPLETED_WITH_ERRORS',
    'COMPLETED_WITH_WARNINGS',
    'CMPL_REPR_ALL_WITH_ERR',
    'CMPL_REPR_PART_WITH_ERR',
    'CMPL REPR ALL WITH WRN',
    'CMPL REPR PART WITH WRN',
  ];

  const isCompletedWithErr = errorStatuses.some((status) =>
    procStatus?.includes(status)
  );
  useEffect(() => {
    const disableCheckbox = getDisabledState(reprocessMethod);
    setDisableCheckbox(disableCheckbox);
    if (disableCheckbox) {
      setTransactionData([]);
    }
  }, [reprocessMethod]);

  const handleTransaction = (action, row, data, checked) => {
    if (!isCompletedWithErr) return;
    const cb = () => {
      setTransactionData((prevTransactions) => {
        if (action === 'CHANGE_OWNER') {
          const ownerTransaction = prevTransactions?.find((transaction) => {
            return (
              transaction.xcptId === row.xcptId &&
              transaction.action === 'CHANGE_OWNER'
            );
          });
          if (ownerTransaction) {
            return prevTransactions.filter((x) => {
              return x.xcptId !== row.xcptId;
            });
          }
          return [
            ...prevTransactions.filter((x) => {
              return (
                x.employeeId !== row.employeeId || x.action === 'CHANGE_OWNER'
              );
            }),
            {
              fileId: row?.transFileId,
              transId: row?.transId,
              transSetId: row?.transSetId,
              xcptId: row?.xcptId,
              employeeId: row?.employeeId,
              distinationSystem,
              action,
            },
          ];
        }
        const transactionExists = prevTransactions?.filter((transaction) => {
          return (
            transaction.employeeId === row.employeeId &&
            transaction.action !== 'CHANGE_OWNER'
          );
        });
        if (!transactionExists?.length) {
          const selectedRecords = (Array.isArray(data) ? data : [])
            .filter((x) => {
              return x.employeeId === row.employeeId;
            })
            .map((record) => {
              return {
                fileId: record?.transFileId,
                transId: record?.transId,
                transSetId: record?.transSetId,
                xcptId: record?.xcptId,
                employeeId: row?.employeeId,
                distinationSystem,
                action,
              };
            });
          return [
            ...prevTransactions.filter((x) => {
              return x.employeeId !== row.employeeId || x.action === action;
            }),
            ...selectedRecords,
          ];
        }
        if (transactionExists?.[0]?.action !== action)
          return prevTransactions
            ?.filter((x) => {
              return x.action !== 'CHANGE_OWNER';
            })
            ?.map((transaction) => {
              return transaction.employeeId === row.employeeId
                ? { ...transaction, action }
                : transaction;
            });

        return prevTransactions?.filter((transaction) => {
          return transaction.employeeId !== row.employeeId;
        });
      });
      modal?.close();
    };
    if (checked && (action === 'REJECT' || action === 'REPROCESS')) {
      modal.open({
        cb,
        title:
          action === 'REJECT'
            ? 'Reject Report'
            : action === 'REPROCESS'
            ? 'Reprocess Report'
            : 'Change Report Ownership',
        action,
      });
      return;
    }
    cb();
  };

  const NameLink = ({ name }) => {
    const url = `${getServiceURL()}/home/help/eligAnalFieldDef#${name}`;

    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: 'white' }}
      >
        {name}
      </a>
    );
  };

  const columns = React.useMemo(() => {
    return [
      {
        Header: <NameLink name="File Processing Date" />,
        accessor: service === 'gps' ? 'fileProcessingDate' : 'processingDt',
        canToggleVisibilty: true,
        isHiddenByDefault: false,
        minWidth: 100,
        width: 100,
        Cell: ({ row, value }) => {
          const processedValue = value.split(' ')[0];

          return cellValueHighLight({
            value: processedValue,
            fileData,
            typeValue: row?.original?.multiTransId,
            changeowner: row?.original?.xcptOwenerChgCd,
          });
        },
      },
      {
        Header: <NameLink name="Severity" />,
        accessor: 'severityCode',
        canToggleVisibilty: true,
        isHiddenByDefault: false,
        minWidth: 80,
        width: 80,
        Cell: ({ row, value }) => {
          return cellValueHighLight({
            value,
            fileData,
            typeValue: row?.original?.multiTransId,
            changeowner: row?.original?.xcptOwenerChgCd,
          });
        },
      },
      {
        Header: <NameLink name="Employee ID" />,
        accessor: 'employeeId',
        canToggleVisibilty: false,
        isHiddenByDefault: service === 'gps' ? true : false,
        Cell: ({ row, value }) => {
          return cellValueHighLight({
            value,
            fileData,
            typeValue: row?.original?.multiTransId,
            changeowner: row?.original?.xcptOwenerChgCd,
          });
        },
      },
      {
        Header: <NameLink name="Medicare ID" />,
        accessor: 'medicareId',
        canToggleVisibilty: false,
        minWidth: 120,
        width: 120,
        isHiddenByDefault: service === 'gps' ? false : true,
        Cell: ({ row, value }) => {
          return cellValueHighLight({
            value,
            fileData,
            typeValue: row?.original?.multiTransId,
            changeowner: row?.original?.xcptOwenerChgCd,
          });
        },
      },
      {
        Header: 'Family ID',
        accessor: 'familyId',
        minWidth: 80,
        width: 80,
        canToggleVisibilty: false,
        isHiddenByDefault: service === 'gps' ? false : true,
        Cell: ({ row, value }) => {
          return cellValueHighLight({
            value,
            fileData,
            typeValue: row?.original?.multiTransId,
            changeowner: row?.original?.xcptOwenerChgCd,
          });
        },
      },
      {
        Header: <NameLink name="Name" />,
        accessor: 'name',
        canToggleVisibilty: true,
        isHiddenByDefault: false,
        minWidth: 100,
        width: 100,
        Cell: ({ row, value }) => {
          return cellValueHighLight({
            value,
            fileData,
            typeValue: row?.original?.multiTransId,
            changeowner: row?.original?.xcptOwenerChgCd,
          });
        },
      },
      {
        Header: <NameLink name="Rel" />,
        accessor: service === 'gps' ? 'mbrTicRelCd' : 'rel',
        canToggleVisibilty: true,
        isHiddenByDefault: service === 'gps' ? true : false,
        minWidth: 50,
        width: 50,
        Cell: ({ row, value }) => {
          return cellValueHighLight({
            value,
            fileData,
            typeValue: row?.original?.multiTransId,
            changeowner: row?.original?.xcptOwenerChgCd,
          });
        },
      },
      {
        Header: <NameLink name="Coverage Type" />,
        accessor: 'covType',
        minWidth: 120,
        width: 120,
        canToggleVisibilty: true,
        isHiddenByDefault: service === 'gps' ? true : false,
        Cell: ({ row, value }) => {
          return cellValueHighLight({
            value,
            fileData,
            typeValue: row?.original?.multiTransId,
            changeowner: row?.original?.xcptOwenerChgCd,
          });
        },
      },
      {
        Header: <NameLink name="Status" />,
        accessor: 'status',
        minWidth: 150,
        width: 150,
        canToggleVisibilty: true,
        isHiddenByDefault: isExtType,
        Cell: ({ row, value }) => {
          return cellValueHighLight({
            value,
            fileData,
            typeValue: row?.original?.multiTransId,
            changeowner: row?.original?.xcptOwenerChgCd,
          });
        },
      },
      {
        Header: <NameLink name="Type" />,
        accessor: 'type',
        canToggleVisibilty: true,
        isHiddenByDefault: false,
        minWidth: 95,
        width: 95,
        Cell: ({ row, value }) => {
          return cellValueHighLight({
            value,
            fileData,
            typeValue: row?.original?.multiTransId,
            changeowner: row?.original?.xcptOwenerChgCd,
          });
        },
      },
      {
        Header: <NameLink name="Warning Name" />,
        accessor: 'warningName',
        canToggleVisibilty: true,
        isHiddenByDefault: false,
        minWidth: 100,
        width: 100,
        Cell: ({ row, value }) => {
          return cellValueHighLight({
            value,
            fileData,
            typeValue: row?.original?.multiTransId,
            changeowner: row?.original?.xcptOwenerChgCd,
          });
        },
      },
      {
        Header: <NameLink name="Data field" />,
        accessor: 'dataField',
        minWidth: 100,
        width: 100,
        canToggleVisibilty: true,
        isHiddenByDefault: false,
        Cell: ({ row, value }) => {
          return cellValueHighLight({
            value,
            fileData,
            typeValue: row?.original?.multiTransId,
            changeowner: row?.original?.xcptOwenerChgCd,
          });
        },
      },
      {
        Header: <NameLink name="Customer Data" />,
        accessor: service === 'gps' ? 'customerData' : 'custData',
        canToggleVisibilty: true,
        isHiddenByDefault: false,
        minWidth: 100,
        width: 100,
        Cell: ({ row, value }) => {
          const maskedValue = maskUsingUserAccess(
            value,
            row?.original?.dataField || '',
            user?.maskingType || 0
          );

          const typeValue = row?.original?.multiTransId;
          return cellValueHighLight({
            value: maskedValue,
            fileData,
            typeValue,
            changeowner: row?.original?.xcptOwenerChgCd,
          });
        },
      },
      {
        Header: <NameLink name="Source Data" />,
        accessor: 'sourceData',
        canToggleVisibilty: true,
        isHiddenByDefault: false,
        minWidth: 100,
        width: 100,
        Cell: ({ row, value }) => {
          const maskedValue = maskUsingUserAccess(
            value,
            row?.original?.dataField || '',
            user?.maskingType || 0
          );

          const typeValue = row?.original?.multiTransId;
          return cellValueHighLight({
            value: maskedValue,
            fileData,
            typeValue,
            changeowner: row?.original?.xcptOwenerChgCd,
          });
        },
      },

      {
        Header: <NameLink name="Transaction Source" />,
        accessor: 'transactionSource',
        canToggleVisibilty: true,
        isHiddenByDefault: service === 'gps' ? true : false,
        minWidth: 100,
        width: 100,
        Cell: ({ row, value }) => {
          const typeValue = row?.original?.multiTransId
            ? row.original.multiTransId.trim()
            : '';
          return cellValueHighLight({
            value,
            fileData,
            typeValue,
            changeowner: row?.original?.xcptOwenerChgCd,
          });
        },
      },
      ,
      ...(isCompletedWithErr && !isExtType
        ? [
            {
              Header: 'Reprocess',
              accessor: 'reprocess',
              Cell: ({ row, data, ...rest }) => {
                if (!(row.original.rel == '18' || row.original.rel == '34'))
                  return null;
                const isChecked = transactionData.some((x) => {
                  return (
                    x?.employeeId === row?.original?.employeeId &&
                    x?.action == 'REPROCESS'
                  );
                });

                const handleReprocess = () => {
                  handleTransaction(
                    'REPROCESS',
                    row.original,
                    data,
                    !isChecked
                  );
                };

                const typeValue = row?.original?.multiTransId?.trim();
                const value = (
                  <Layout.Group>
                    <Checkbox
                      label=""
                      name={`reprocess-${row.original.id}`}
                      isChecked={isChecked}
                      onChange={handleReprocess}
                      isDisabled={disableCheckbox}
                    />
                  </Layout.Group>
                );

                return cellValueHighLight({
                  value,
                  fileData,
                  changeowner: row?.original?.xcptOwenerChgCd,
                  typeValue,
                });
              },
              minWidth: 90,
              width: 90,
              isHiddenByDefault: service === 'gps' ? true : false,
            },
            {
              Header: 'Reject',
              accessor: 'reject',
              Cell: ({ row, data }) => {
                if (!(row.original.rel == '18' || row.original.rel == '34'))
                  return null;
                const isChecked = transactionData.some((x) => {
                  return (
                    x?.employeeId === row?.original?.employeeId &&
                    x?.action == 'REJECT'
                  );
                });
                const handleReject = () => {
                  handleTransaction('REJECT', row.original, data, !isChecked);
                };

                const typeValue = row?.original?.multiTransId?.trim();
                const value = (
                  <Layout.Group>
                    <Checkbox
                      label=""
                      name={`reject-${row.original.id}`}
                      onChange={handleReject}
                      isChecked={isChecked}
                      isDisabled={disableCheckbox}
                    />
                  </Layout.Group>
                );

                return cellValueHighLight({
                  value,
                  fileData,
                  typeValue,
                  changeowner: row?.original?.xcptOwenerChgCd,
                });
              },
              minWidth: 75,
              width: 75,
              isHiddenByDefault: service === 'gps' ? true : false,
            },
            {
              Header: 'Change Owner',
              accessor: 'changeOwner',
              Cell: ({ row, data }) => {
                const isChecked = transactionData.some((x) => {
                  return (
                    x?.xcptId === row?.original?.xcptId &&
                    x?.action == 'CHANGE_OWNER'
                  );
                });
                const handleChangeOwner = () => {
                  handleTransaction(
                    'CHANGE_OWNER',
                    row.original,
                    data,
                    !isChecked
                  );
                };

                const typeValue = row?.original?.multiTransId?.trim();
                const value = (
                  <Layout.Group>
                    <Checkbox
                      label=""
                      name={`changeOwner-${row.original.id}`}
                      onChange={handleChangeOwner}
                      isChecked={isChecked}
                      isDisabled={disableCheckbox}
                    />
                  </Layout.Group>
                );

                return cellValueHighLight({
                  value,
                  changeowner: row?.original?.xcptOwenerChgCd,
                  fileData,
                  typeValue,
                });
              },
              minWidth: 100,
              width: 100,
              isHiddenByDefault: service === 'gps' ? true : false,
            },
          ]
        : []),
      {
        Header: 'Detail View',
        accessor: 'detailView',
        enableSorting: false,
        canToggleVisibilty: true,
        isHiddenByDefault: false,
        minWidth: 90,
        width: 90,
        Cell: ({ row }) => {
          const nameSplit = row?.original?.name.split(',');
          const firstName = nameSplit[1].trim();
          const lastName = nameSplit[0].trim();
          const typeValue = row?.original?.multiTransId?.trim();
          const value =
            service !== 'gps' &&
            fileData?.multilevelSubmitter &&
            typeValue?.length > 0
              ? 'View'
              : 'Detail View';

          const link =
            service === 'gps'
              ? `/home/gps-detail-view/`
              : fileData?.multilevelSubmitter && typeValue?.length > 0
              ? `/home/multi-level-view/`
              : `/home/detail-view/`;

          const linkData = {
            fileID: fileData?.fileID || fileData.fileId,
            transSetId: fileData?.transSetId,
            medicareId: row?.original?.medicareId,
            firstName: firstName,
            lastName: lastName.trim(),
            processingDt: fileData?.processingDt,
            customerName: fileData?.customerName,
            mbrTicRelCd: row?.original?.mbrTicRelCd,
            transId: row?.original?.transId,
            subscrId: row?.original?.subscrId,
            sbmtGrpName: fileData?.sbmtGrpName,
            relationship: row?.original?.rel,
            cescustnbr: fileData?.cescustnbr,
            sbmtGrpId: fileData?.sbmtGrpId,
            empId: row?.original?.employeeId,
            reportType: 'IE',
          };

          const encryptedData = CryptoJS.AES.encrypt(
            JSON.stringify(linkData),
            encdata
          ).toString();

          const newUrl = `${link}${encodeURIComponent(encryptedData)}`;

          return cellValueHighLight({
            value: (
              <Flex
                css={{
                  gap: '10px',
                }}
              >
                <Link
                  size="$sm"
                  target="_blank"
                  href={newUrl}
                  onClick={(e) => {
                    e.preventDefault();
                    window.open(newUrl, 'DetailViewTab');
                  }}
                >
                  <Button
                    variant="outline"
                    size="$sm"
                    css={{
                      fontSize: '12px',
                    }}
                  >
                    <strong>{value}</strong>
                  </Button>
                </Link>
              </Flex>
            ),
            fileData,
            typeValue,
            changeowner: row?.original?.xcptOwenerChgCd,
          });
        },
      },
    ];
  }, [
    fileData?.allDate,
    isCompletedWithErr,
    transactionData,
    disableCheckbox,
    user,
  ]);

  const handleButtonClick = (type) => {
    setActionType(type);
    setIsPromptVisible(true);
  };

  const handleConfirm = async () => {
    setIsPromptVisible(false);
    await handleSubmit(actionType);
  };

  const handleCancel = () => {
    setIsPromptVisible(false);
  };
  const handleSubmit = async (actionType) => {
    if (!isCompletedWithErr) return;
    setIsLoading(true);
    try {
      switch (actionType) {
        case 'Checked Transactions': {
          const response = await trigger(transactionData);
          router.navigate('/');
          return;
        }
        case 'Reprocess All': {
          await allTrigger({
            sbmtGrpId: fileData?.sbmtGrpId || '',
            fileId: fileData?.fileID || '',
            transSetId: fileData?.transSetId || '',
            flProcDttm: fileData?.processingDt || '',
            action: 'EDM_REPROCESS_ALL',
            errortype: fieldCode || '',
            allDates: fileData?.allDate || 'false',
            platform: service === 'gps' ? 'GPS' : 'CDB',
            errorCode: errorCode || '',
          });
          router.navigate('/');
          return;
        }
        // case 'Reprocess All eligible EEA Errors': {
        //   await allTrigger({
        //     sbmtGrpId: fileData?.sbmtGrpId || '',
        //     fileId: fileData?.fileID || '',
        //     transSetId: fileData?.transSetId || '',
        //     flProcDttm: fileData?.processingDt || '',
        //     action: 'reprocessalleaa',
        //   });
        //   router.navigate('/');
        // }

        case 'Reject All': {
          await allTrigger({
            sbmtGrpId: fileData?.sbmtGrpId || '',
            fileId: fileData?.fileID || '',
            transSetId: fileData?.transSetId || '',
            flProcDttm: fileData?.processingDt || '',
            action: 'EDM_REJECT_ALL',
            errortype: fieldCode || '',
            errorCode: errorCode || '',
            allDates: fileData?.allDate || 'false',
            platform: service === 'gps' ? 'GPS' : 'CDB',
          });
          router.navigate('/');
          return;
        }
        case 'Change Owner All': {
          await allTrigger({
            sbmtGrpId: fileData?.sbmtGrpId || '',
            fileId: fileData?.fileID || '',
            transSetId: fileData?.transSetId || '',
            flProcDttm: fileData?.processingDt || '',
            action: 'EDM_CHANGE_OWNER_ALL',
            errortype: fieldCode || '',
            errorCode: errorCode || '',
            allDates: fileData?.allDate || 'false',
            platform: service === 'gps' ? 'GPS' : 'CDB',
          });
          router.navigate('/');
          return;
        }
        default: {
          alert('Other operations are under construction!');
        }
      }
    } catch (e) {
      console.log(e);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card
      css={{
        'abyss-card-root': { width: '100%', padding: '0', borderRadius: '4px' },
      }}
    >
      <div
        style={{
          backgroundColor:
            'var(--abyss-colors-web-semantic-color-surface-container-primary)',
          color:
            'var(--abyss-colors-web-semantic-color-text-content-primaryAlt)',
          fontSize: '18px',
          padding: '24px',
          borderTopLeftRadius: '3px',
          borderTopRightRadius: '3px',
          fontWeight: 'bold',
        }}
      >
        Eligibility Analyst Corrections Required
      </div>
      <Box
        css={{
          '.abyss-data-table-custom-header-section': {
            flexGrow: 1,
          },
        }}
      >
        {isLoading && <LoadingScreen />}

        <Table
          columns={columns}
          dataKey="eligibilityAnalystCorrections"
          fetch={
            service === 'gps'
              ? fieldCode && fieldCode.length > 1
                ? useLazyGetGpsENWChildReportsQuery
                : useLazyGetGPSReportsQuery
              : useLazyGetReportsQuery
          }
          requestParams={{
            ...router.getRouteParams(),
            ...fileData,
          }}
          customHeaderSection={
            fileData?.allDate === 'false' && isCompletedWithErr ? (
              //<form onSubmit={handleSubmit}>
              <Layout.Group
                css={{
                  gap: '20px',
                  alignItems: 'flex-end',
                  '.abyss-select-input-root': {
                    minWidth: '288px',
                  },
                }}
              >
                {user?.cmplRepr ? (
                  <SelectInput
                    label="Reprocess Method"
                    value={reprocessMethod}
                    onChange={(value) => {
                      return setReprocessMethod(value);
                    }}
                    options={[
                      {
                        value: 'Checked Transactions',
                        label: 'Checked Transactions',
                      },
                      {
                        value: 'Reprocess All',
                        label: 'Reprocess All',
                      },
                      // {
                      //   value: 'Reprocess All eligible EEA Errors',
                      //   label: 'Reprocess All eligible EEA Errors',
                      // },
                      { value: 'Reject All', label: 'Reject All' },
                      {
                        value: 'Change Owner All',
                        label: 'Change Owner All',
                      },
                    ]}
                  />
                ) : null}

                {user?.cmplRepr ? (
                  <React.Fragment>
                    <Button
                      size="$sm"
                      isDisabled={isTableLoading}
                      onClick={() => handleButtonClick(reprocessMethod)}
                    >
                      Submit
                    </Button>
                    <Button
                      size="$sm"
                      type="button"
                      variant="filled"
                      color="destructive"
                      isDisabled={isTableLoading}
                      onClick={() => {
                        setReprocessMethod('Checked Transactions');
                        setTransactionData([]);
                      }}
                      style={{ marginLeft: '8px' }}
                    >
                      clear
                    </Button>
                  </React.Fragment>
                ) : null}
              </Layout.Group>
            ) : //</form>
            null
          }
          showGlobalFilter={true}
          onLoadingChange={setIsTableLoading}
          downloadMenuItems={[
            {
              title: 'Download CSV',
              onClick: 'exportVisibleData',
              csvFilename: `eligibility-analyst-corrections-${service}.csv`,
            },
          ]}
        />
        <ModalDialog
          header={modalData?.title || ''}
          model="process-modal"
          onClose={() => {
            return modal?.close();
          }}
        >
          <div>
            <p>
              Reminder: By selecting this employee the entire family will
              be&nbsp;
              {modalData?.action === 'reject' ? 'rejected' : 'reprocessed'}.
            </p>
          </div>
          <div>
            <Layout.Group alignLayout="right">
              <Button
                onClick={() => {
                  modalData?.cb();
                }}
              >
                Okay
              </Button>
            </Layout.Group>
          </div>
        </ModalDialog>
        <ModalDialog
          isOpen={isPromptVisible}
          onClose={handleCancel}
          header="Confirmation"
        >
          <div>
            <div className="popup">
              <p>Are you sure you want to proceed with this action?</p>
              <Box
                css={{
                  padding: 0,
                  background: 'transparent',
                  display: 'flex',
                  gap: '12px',
                  marginTop: '20px',
                  alignItems: 'center',
                }}
              >
                <Button onClick={handleConfirm}>Yes</Button>
                <Button
                  onClick={handleCancel}
                  variant="filled"
                  color="destructive"
                >
                  No
                </Button>
              </Box>
            </div>
          </div>
        </ModalDialog>
      </Box>
    </Card>
  );
};
